import { getFormattedDate } from '../../utils';
import { getLeaderboardGranularity } from '../hooks/useLeaderboardQueries';
import { flatten, formatGeneric, formatTranslations, getDate, zipLocales } from './searchItemFormatting';

const getLocales = ( item ) => {
    try {
        const result = item.name.map( i => {
            return {
                code: i.locale,
                name: i.content === null ? '' : i.content,
                description: item.description.find( d => d.locale === i.locale )?.content || ''
            };
        } );

        return result;
    } catch ( error ) {
        console.error( 'Error parsing locales', error );
        return [];
    }
};

const setEnglishNameDescription = ( item ) => { 
    try {
        if ( item.locales.length > 0 ) { 
        
            const locale = item.locales.find( l => l.code === 'en-US' ) || item.locales[ 0 ];
                    
            if ( locale ) {
                item.name = locale.name;
                item.content = locale.description;
                item._selectedLocale = locale.code;
            } else { 
                console.error( 'Error, cannot find locale for objective', item );
            }
        }
    } catch ( error ) {
        console.error( 'Error getting english name and description', error );
    }
};

const parseLearningObjects = ( data ) => {
    try {
        const result = data.search.edges.map( i => {
            const id = i.node.learningObjectId; 
                
            const item = {
                id,
                key: id,
                locales: getLocales( i.node ),
                name: '',
                content: '',
                createdAt: getFormattedDate( i.node.createdAt ),
                updatedAt: getFormattedDate( i.node.updatedAt ), 
                format: i.node.format, 
                courseCount: i.node.courseCount, 
                usedByCourses: i.node.usedByCourses !== null ? 
                    i.node.usedByCourses.map( i => ( { id: i.id || i.uuid, name: i.name } ) ) : [],
                _selectedLocale: 'en-US' 
            };      
                                    
            setEnglishNameDescription( item );
            
            return item;
        } );

        return result;
    } catch ( error ) {
        console.error( 'Error parsing learning objects', error );
        return [];
    } 
};

const parseLeaderboards = ( data ) => {
    try {
        const result = flatten( data.search )
            .map( formatGeneric )
            .map( l => ( {
                id: l.leaderboardId,
                key: l.leaderboardId,
                name: l.defaultName,
                description: l.defaultDescription,
                endAt: getDate( l.endAt ),
                createdAt: l.createdAt,
                timezone: l.timezone,
                isPublished: l.isPublished,
                updatedAt: l.updatedAt,
                locales: formatTranslations( zipLocales( l ) ),
                granularity: l.granularity,
                granularities: getLeaderboardGranularity( l.granularity ),
                startAt: getDate( l.startAt ),
                leaderboardType: l.leaderboardType,
                leaderboardTypeId: l.leaderboardTypeId,
                audiences: l.leaderboardAudiences?.length > 0 ? l.leaderboardAudiences.map( formatGeneric ).map( a => ( {
                    id: a.leaderboardAudienceId,
                    name: a.defaultName,
                    description: a.defaultDescription,
                    locales: formatTranslations( zipLocales( a ) ),
                    originalData: a,
                } ) ) : [],
                originalData: l,
            } ) );
        return result;
    } catch ( e ) {
        console.log( e );
        return [];
    }
};

const parseCourses = ( data ) => {
    try {
        const result = data.search.edges.map( i => {
            const id = i.node.courseId; 
            const locales = getLocales( i.node );

            const item = {
                id,
                key: id,
                locales: locales,
                name: '',
                content: '', 
                isILT: i.node.isIlt,
                createdAt: getFormattedDate( i.node.createdAt ),
                updatedAt: getFormattedDate( i.node.updatedAt ), 
                brandIds: i.node.lxp.relatedResourceBrandIds === null ? [] : i.node.lxp.relatedResourceBrandIds,
                tags: i.node.lxp.tags || [],
                isPublished: i.node.isPublished || false,
                isFeatured: i.node.isFeatured || false,
                isSequential: i.node.isSequential || false,
                thumbnailImage: i.node.thumbnailImage === null ? '' : i.node.thumbnailImage.url,
                learningObjects: i.node.learningObjects !== null ? 
                    i.node.learningObjects.map( lo => {
                        return {
                            id: lo.learningObjectId,
                            key: lo.learningObjectId,
                            format: lo.format,
                            name: lo.name.find( n => n.locale === 'en-US' ).content || 'No name',
                        };
                    } ) : [],
                prerequisiteOf: i.node.lxp.prerequisiteStatus.isPrerequisiteOf,
                prerequisites: ( i.node.lxp.prerequisiteSet?.prerequisites || [] )
                    .map( p => p.objectId ),
                prerequisiteOperator: i.node.lxp.prerequisiteSet?.operator,
                _selectedLocale: 'en-US' 
            }; 

            setEnglishNameDescription( item );
            
            return item;
        } );

        return result;
    } catch ( error ) {
        console.error( 'Error parsing courses', error );
        return [];
    } 
};

const parseCurricula = ( data ) => {
    try {
        const result = data.search.edges.map( i => {
            const id = i.node.curriculumId; 
                
            const item = {
                id,
                key: id,
                locales: getLocales( i.node ),
                name: '',
                content: '', 
                createdAt: getFormattedDate( i.node.createdAt ),
                updatedAt: getFormattedDate( i.node.updatedAt ), 
                brandIds: i.node.lxp.relatedResourceBrandIds === null ? [] : i.node.lxp.relatedResourceBrandIds,
                tags: i.node.lxp.tags || [],
                weight: i.node.weight,
                thumbnailImage: i.node.thumbnailImage === null ? '' : i.node.thumbnailImage.url,
                courses: i.node.courses !== null ? 
                    i.node.courses.map( lo => {
                        return {
                            id: lo.courseId,
                            key: lo.courseId,
                            name: lo.name.find( n => n.locale === 'en-US' ).content || 'No name',
                        };
                    } ) : [],
                prerequisiteOf: i.node.lxp.prerequisiteStatus.isPrerequisiteOf,
                _selectedLocale: 'en-US' 
            }; 

            setEnglishNameDescription( item );
            
            return item;
        } );

        return result;
    } catch ( error ) {
        console.error( 'Error parsing curricula', error );
        return [];
    } 
};

const parseResources = ( data ) => {
    try {
        const result = data.search.edges.map( i => {
            const id = i.node.resourceId; 
                
            const item = {
                id,
                key: id,
                locales: getLocales( i.node ),
                name: '',
                content: '',
                isPublished: i.node.isPublished,
                createdAt: getFormattedDate( i.node.createdAt ),
                updatedAt: getFormattedDate( i.node.updatedAt ), 
                brandIds: i.node.lxp.relatedResourceBrandIds,
                localizations: i.node.resourceLocalizations,
                type: i.node.type,
                isFeatured: i.node.isFeatured,
                image: i.node.image,
                _selectedLocale: 'en-US' 
            }; 

            setEnglishNameDescription( item );
            
            return item;
        } );

        return result;
    } catch ( error ) {
        console.error( 'Error parsing resources', error );
        return [];
    } 
};

const parseNotifications = ( data ) => {
    try {
        const result = data.search.edges.map( i => {
            const id = i.node.notificationId; 
                
            const item = {
                id,
                key: id,
                locales: getLocales( i.node ),
                name: '',
                content: '',
                weight: i.node.weight,
                isSticky: i.node.isSticky, 
                isFeatured: i.node.isFeatured,
                isPublished: i.node.isPublished,
                createdAt: getFormattedDate( i.node.createdAt ),
                updatedAt: getFormattedDate( i.node.updatedAt ), 
                brandIds: i.node.lxp.relatedResourceBrandIds === null ? [] : i.node.lxp.relatedResourceBrandIds,
                publishAt: getFormattedDate( i.node.publishAt ),
                unpublishAt: getFormattedDate( i.node.unpublishAt ), 
                unfeatureAt: getFormattedDate( i.node.unfeatureAt ),
                _selectedLocale: 'en-US' 
            }; 
 
            setEnglishNameDescription( item );
            
            return item;
        } );

        return result;
    } catch ( error ) {
        console.error( 'Error parsing notifications', error );
        return [];
    } 
};

const parseObjectives = ( data ) => {
    try {
        const result = data.search.edges.map( i => {
            const id = i.node.objectiveId; 
           
            const item = {
                id,
                key: id,
                locales: getLocales( i.node ),
                name: '',
                content: '',
                createdAt: getFormattedDate( i.node.createdAt ),
                updatedAt: getFormattedDate( i.node.updatedAt ),
                isAvailable: i.node.isAvailable,
                availableAt: i.node.availableAt === null ? '' : getFormattedDate( i.node.availableAt ),
                hasRevision: i.node.revisionId !== null,
                isPublished: i.node.isPublished,
                isRepeatable: i.node.isRepeatable,
                brandIds: i.node.lxp.relatedResourceBrandIds === null ? [] : i.node.lxp.relatedResourceBrandIds,
                points: i.node.points ?? 0,
                credits: i.node.credits ?? 0,
                badgeId: i.node.badgeRewardId ?? null,
                publishAt: i.node.publishAt === null ? '' : getFormattedDate( i.node.publishAt ),
                unavailableAt: i.node.unavailableAt === null ? '' : getFormattedDate( i.node.unavailableAt ),
                criteria: i.node.criteria.map( i => { 
                    return {
                        id: i.criterionId,
                        key: i.criterionId,
                        nameDefault: i.name.find( n => n.locale === 'en-US' )?.content || i.name[ 0 ].content,
                        ...i
                    };
                } ),
                _selectedLocale: 'en-US',
            }; 

            setEnglishNameDescription( item );

            return item;
        } );

        return result;
    } catch ( error ) {
        console.error( 'Error parsing objectives', error );
        return [];
    } 
};

const parseCategories = ( data ) => {
    try {
        const result = data.search.edges.map( i => {
            const id = i.node.categoryId; 

            const item = {
                id,
                key: id,
                locales: getLocales( i.node ),
                name: '',
                content: '',
                sortOrder: i.node.sortOrder,
                createdAt: getFormattedDate( i.node.createdAt ),
                updatedAt: getFormattedDate( i.node.updatedAt ),
                brandId: i.node.brand?.brandId, // TODO, may not need anymore
                brandName: i.node.brand?.name,  // TODO, may not need anymore
                brandIds: i.node.lxp.relatedResourceBrandIds === null ? [] : i.node.lxp.relatedResourceBrandIds,
                courseIds: i.node.courseIds,
                curriculumIds: i.node.curriculumIds,
                _selectedLocale: 'en-US',
            }; 

            setEnglishNameDescription( item );

            return item;
        } );

        return result;
    } catch ( error ) {
        console.error( 'Error parsing categories', error );
        return [];
    } 
};

const parseBadges = ( data ) => {
    try {
        const result = flatten( data.search )
            .map( formatGeneric )
            .map( b => ( {
                id: b.badgeId,
                key: b.badgeId,
                name: b.defaultName,
                description: b.defaultDescription,
                personBadgeCount: b.personBadgeCount,
                isRepeatable: b.isRepeatable,
                createdAt: b.createdAt,
                isPublished: b.isPublished,
                updatedAt: b.updatedAt,
                image: b.badgeImage,
                locales: formatTranslations( zipLocales( b ) ),
            } ) );

        return result;
    } catch ( error ) {
        console.error( 'Error parsing badges', error );
        return [];
    } 
};

const parseSearchQueryData = ( { type, data } ) => {      
    if ( type === 'categories' ) {
        return parseCategories( data );
    }

    if ( type === 'objectives' ) {
        return parseObjectives( data );
    }

    if ( type === 'notifications' ) {
        return parseNotifications( data );
    }
    
    if ( type === 'objects' ) {
        return parseLearningObjects( data );
    }
    
    if ( type === 'resources' ) {
        return parseResources( data );
    }
    
    if ( type === 'courses' ) {
        return parseCourses( data );
    }

    if ( type === 'curricula' ) {
        return parseCurricula( data );
    }

    if ( type === 'leaderboards' ) {
        return parseLeaderboards( data );
    }

    if ( type === 'badges' ) {
        return parseBadges( data );
    }
    
    return [];
};

export default parseSearchQueryData;
